// src/components/LecturerSidebar.js
import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Home,
  Users,
  BookOpen,
  LogIn,
  Calendar,
  GraduationCap,
  ClipboardList,
  ListCheck,
  Info,
  User,              // ⬅️ NOWA IKONA
} from 'lucide-react';
import '../styles/Sidebar.css';

export default function Sidebar() {
  const [role, setRole] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    async function fetchRole() {
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        try {
          const user = JSON.parse(cachedUser);
          if (user?.role) {
            setRole(user.role);
          }
        } catch (e) {}
      }

      const token = localStorage.getItem('token');
      if (!token && !cachedUser) {
        navigate('/');
        return;
      }

      const requestConfig = {
        params: { _: Date.now() },
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 304,
        withCredentials: true
      };

      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
      }

      try {
        const response = await axios.get('/auth/me', requestConfig);
        if (!isMounted) return;

        if (response.status === 304) {
          if (cachedUser) {
            try {
              const user = JSON.parse(cachedUser);
              if (user?.role) {
                setRole(user.role);
                return;
              }
            } catch (e) {}
          }
          return;
        }

        const nextRole = response.data?.user?.role;
        if (nextRole) {
          setRole(nextRole);
          if (cachedUser) {
            try {
              const user = JSON.parse(cachedUser);
              user.role = nextRole;
              localStorage.setItem('user', JSON.stringify(user));
            } catch (e) {}
          }
        }
      } catch (error) {
        console.error('Nie udało się pobrać roli użytkownika', error);

        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/');
        }
      }
    }

    fetchRole();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const getRoleLabel = (role) => {
    switch (role) {
      case 'administrator':
        return 'Administrator';
      case 'student':
        return 'Kursant';
      case 'lecturer':
        return 'Wykładowca';
      default:
        return '';
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      console.error('Błąd podczas wylogowywania', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
    }
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="sidebar">
      {/* HEADER + HAMBURGER */}
      <div className="sidebar-header">
        <div className="sidebar-logo-icon">
          <img
            src="/images/logo.svg"
            alt="Logo szkoły"
            className="logo-image"
          />
        </div>
        <div className="sidebar-header-title">
          <h3 className="header-title">PSDiA</h3>
          <p className="header-subtitle">{getRoleLabel(role)}</p>
        </div>

        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Otwórz menu"
        >
          <span className="sidebar-toggle-bar" />
          <span className="sidebar-toggle-bar" />
          <span className="sidebar-toggle-bar" />
        </button>
      </div>

      {/* MENU */}
      <div className={`sidebar-main ${menuOpen ? 'open' : ''}`}>
        <nav>
          <NavLink
            to="/lecturer/dashboard"
            className={({ isActive }) =>
              'main-menu' + (isActive ? ' active' : '')
            }
            onClick={closeMenu}
          >
            <Home size={20} />
            <span>Panel Główny</span>
          </NavLink>

          <NavLink
            to="/lecturer/students"
            className={({ isActive }) =>
              'main-menu' + (isActive ? ' active' : '')
            }
            onClick={closeMenu}
          >
            <Users size={20} />
            <span>Moi Kursanci</span>
          </NavLink>

          <NavLink
            to="/lecturer/sessions"
            className={({ isActive }) =>
              'main-menu' + (isActive ? ' active' : '')
            }
            onClick={closeMenu}
          >
            <Calendar size={20} />
            <span>Kalendarz zajęć</span>
          </NavLink>

          <NavLink
            to="/lecturer/messages"
            className={({ isActive }) =>
              'main-menu' + (isActive ? ' active' : '')
            }
            onClick={closeMenu}
          >
            <Info size={20} />
            <span>Wiadomości</span>
          </NavLink>

          <NavLink
            to="/lecturer/tasks"
            className={({ isActive }) =>
              'main-menu' + (isActive ? ' active' : '')
            }
            onClick={closeMenu}
          >
            <ListCheck size={20} />
            <span>Zadania</span>
          </NavLink>

          <NavLink
            to="/lecturer/grades"
            className={({ isActive }) =>
              'main-menu' + (isActive ? ' active' : '')
            }
            onClick={closeMenu}
          >
            <GraduationCap size={20} />
            <span>Oceny</span>
          </NavLink>

          <NavLink
            to="/lecturer/materials"
            className={({ isActive }) =>
              'main-menu' + (isActive ? ' active' : '')
            }
            onClick={closeMenu}
          >
            <BookOpen size={20} />
            <span>Materiały</span>
          </NavLink>

          <NavLink
            to="/lecturer/attendance"
            className={({ isActive }) =>
              'main-menu' + (isActive ? ' active' : '')
            }
            onClick={closeMenu}
          >
            <ClipboardList size={20} />
            <span>Obecności</span>
          </NavLink>

          {/* NOWA POZYCJA – MÓJ PROFIL */}
          <NavLink
            to="/lecturer/profile"
            className={({ isActive }) =>
              'main-menu' + (isActive ? ' active' : '')
            }
            onClick={closeMenu}
          >
            <User size={20} />
            <span>Mój profil</span>
          </NavLink>
        </nav>

        <div className="logout-section">
          <div
            className="nav-item-logout"
            onClick={() => {
              closeMenu();
              handleLogout();
            }}
          >
            <LogIn size={20} />
            <span>Wyloguj</span>
          </div>
        </div>
      </div>
    </div>
  );
}
